import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Avatar,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { PhotoCamera, Folder as FolderIcon } from '@mui/icons-material';
import api from '../../services/api';

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const EMPTY_FORM = {
  name: '',
  description: '',
  status: 'planning',
  avatar_url: '',
  is_public: false,
  task_default_public: false,
};

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { createProject, loading: contextLoading, error: contextError } = useProject();
  const { currentOrganization } = useOrganization();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loadingProject, setLoadingProject] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoadingProject(true);
    api.get(`/projects/${id}`)
      .then((res) => {
        if (cancelled) return;
        const p = res.data;
        setFormData({
          name: p.name || '',
          description: p.description || '',
          status: p.status || 'planning',
          avatar_url: p.avatar_url || '',
          is_public: Boolean(p.is_public),
          task_default_public: Boolean(p.task_default_public),
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load project');
      })
      .finally(() => {
        if (!cancelled) setLoadingProject(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (!id && !currentOrganization) return;

    setError(null);
    setSaving(true);
    try {
      const payload = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description.trim(),
        avatar_url: formData.avatar_url.trim() || null,
      };

      if (id) {
        const res = await api.put(`/projects/${id}`, payload);
        const updated = res.data?.project || res.data;
        navigate(`/projects/${updated?.id || id}`);
      } else {
        const created = await createProject({ ...payload, org_id: currentOrganization.id });
        const newId = created?.id || created?.project?.id;
        navigate(newId ? `/projects/${newId}` : '/projects');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const isLoading = loadingProject || contextLoading;
  const displayError = error || contextError;

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {id ? 'Edit Project' : 'Create New Project'}
        </Typography>

        {!currentOrganization && !id && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please select an organization before creating a project.
          </Alert>
        )}

        {displayError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {displayError}
          </Alert>
        )}

        {isLoading && !formData.name ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {/* Avatar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar src={formData.avatar_url} sx={{ width: 72, height: 72, bgcolor: 'secondary.main' }}>
                {!formData.avatar_url && <FolderIcon sx={{ fontSize: 36 }} />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Project Avatar (Optional)
                </Typography>
                <Tooltip title="Paste a publicly accessible image URL (JPG, PNG, or SVG)" placement="right">
                  <TextField
                    fullWidth
                    size="small"
                    name="avatar_url"
                    placeholder="https://example.com/avatar.png"
                    value={formData.avatar_url}
                    onChange={handleChange}
                    InputProps={{ startAdornment: <PhotoCamera sx={{ mr: 1, color: 'action.active' }} /> }}
                  />
                </Tooltip>
              </Box>
            </Box>

            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Project Name"
              name="name"
              autoFocus={!id}
              value={formData.name}
              onChange={handleChange}
              disabled={isLoading}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              fullWidth
              id="description"
              label="Description"
              name="description"
              multiline
              minRows={4}
              value={formData.description}
              onChange={handleChange}
              disabled={isLoading}
              sx={{ mb: 2 }}
            />

            <TextField
              select
              margin="normal"
              fullWidth
              id="status"
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              disabled={isLoading}
              sx={{ mb: 3 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
              <Tooltip title="Public projects are visible to anyone with the link. Private projects are only accessible to invited team members." placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      name="is_public"
                      checked={formData.is_public}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  }
                  label={`Visibility: ${formData.is_public ? 'Public' : 'Private'}`}
                />
              </Tooltip>
              <Tooltip title="When enabled, new features and work items will be public by default. You can still change visibility on individual items." placement="right">
                <FormControlLabel
                  control={
                    <Switch
                      name="task_default_public"
                      checked={formData.task_default_public}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                  }
                  label={`Work Items Default: ${formData.task_default_public ? 'Public' : 'Private'}`}
                />
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(id ? `/projects/${id}` : '/projects')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || isLoading || (!id && !currentOrganization)}
                startIcon={saving ? <CircularProgress size={20} /> : null}
              >
                {saving
                  ? (id ? 'Saving…' : 'Creating…')
                  : (id ? 'Save Changes' : 'Create Project')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ProjectForm;
